/**
 * Service Firebase Direct - Remplace les appels API Python
 * Migration vers Firebase uniquement (plus de backend intermédiaire)
 */
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  orderBy,
  limit,
  writeBatch,
  serverTimestamp,
  Timestamp,
  runTransaction
} from 'firebase/firestore';
import { lineQuantity, lineRevenue } from '../utils/orderLines';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '../firebase/config';

// ==================== HELPERS ====================

const serializeTimestamps = (data) => {
  const result = { ...data };
  for (const key of Object.keys(result)) {
    if (result[key] instanceof Timestamp) {
      result[key] = result[key].toDate().toISOString();
    }
  }
  return result;
};

const docsToArray = (snapshot) => {
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...serializeTimestamps(doc.data())
  }));
};

// ==================== CATEGORIES ====================
export const categoriesService = {
  getAll: async () => {
    // Ne pas utiliser orderBy car les docs sans display_order seraient exclus
    const snapshot = await getDocs(collection(db, 'categories'));
    const categories = docsToArray(snapshot);
    // Trier côté client (plus fiable)
    categories.sort((a, b) => (a.display_order || a.order || 0) - (b.display_order || b.order || 0));
    return { categories };
  },

  getOne: async (id) => {
    const docRef = await getDoc(doc(db, 'categories', id));
    if (!docRef.exists()) return null;
    return { category: { id: docRef.id, ...serializeTimestamps(docRef.data()) } };
  },

  create: async (data) => {
    const docRef = await addDoc(collection(db, 'categories'), {
      ...data,
      display_order: data.display_order || data.order || 0,
      order: data.display_order || data.order || 0,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp()
    });
    return { success: true, category: { id: docRef.id, ...data } };
  },

  update: async (id, data) => {
    const updateData = { ...data, updated_at: serverTimestamp() };
    // Normaliser order/display_order
    if (data.order !== undefined || data.display_order !== undefined) {
      updateData.order = data.display_order || data.order;
      updateData.display_order = data.display_order || data.order;
    }
    await updateDoc(doc(db, 'categories', id), updateData);
    return { success: true, category: { id, ...data } };
  },

  delete: async (id) => {
    await deleteDoc(doc(db, 'categories', id));
    return { success: true };
  },

  reorder: async (orders) => {
    const batch = writeBatch(db);
    orders.forEach(item => {
      const ref = doc(db, 'categories', item.id);
      batch.update(ref, {
        display_order: item.display_order || item.order || 0,
        order: item.display_order || item.order || 0,
        updated_at: serverTimestamp()
      });
    });
    await batch.commit();
    return { success: true };
  }
};

// ==================== PRODUCTS ====================
export const productsService = {
  getAll: async (category_id = null) => {
    // Charger tous les produits puis filtrer/trier côté client
    // (évite les problèmes d'index et de champs manquants)
    const snapshot = await getDocs(collection(db, 'products'));
    let products = docsToArray(snapshot);

    // BK-32: Exclure les produits soft-deleted
    products = products.filter(p => !p.is_deleted);

    if (category_id) {
      products = products.filter(p => p.category_id === category_id);
    }

    products.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    return { products };
  },

  getOne: async (id) => {
    const docRef = await getDoc(doc(db, 'products', id));
    if (!docRef.exists()) return null;
    return { product: { id: docRef.id, ...serializeTimestamps(docRef.data()) } };
  },

  create: async (data) => {
    const docRef = await addDoc(collection(db, 'products'), {
      ...data,
      display_order: data.display_order || 0,
      is_available: data.is_available !== false,
      is_out_of_stock: data.is_out_of_stock || false,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp()
    });
    return { success: true, product: { id: docRef.id, ...data } };
  },

  update: async (id, data) => {
    await updateDoc(doc(db, 'products', id), {
      ...data,
      updated_at: serverTimestamp()
    });
    return { success: true, product: { id, ...data } };
  },

  delete: async (id) => {
    // BK-32: Soft-delete (pas de suppression physique)
    await updateDoc(doc(db, 'products', id), {
      is_deleted: true,
      deleted_at: serverTimestamp()
    });
    return { success: true };
  },

  toggleStock: async (id, is_out_of_stock) => {
    await updateDoc(doc(db, 'products', id), {
      is_out_of_stock,
      updated_at: serverTimestamp()
    });
    return { success: true };
  },

  toggleAvailability: async (id, is_available) => {
    await updateDoc(doc(db, 'products', id), {
      is_available,
      updated_at: serverTimestamp()
    });
    return { success: true };
  },

  updateSortOrder: async (items) => {
    const batch = writeBatch(db);
    items.forEach(item => {
      const ref = doc(db, 'products', item.id);
      batch.update(ref, {
        display_order: item.display_order,
        updated_at: serverTimestamp()
      });
    });
    await batch.commit();
    return { success: true };
  }
};

// ==================== OPTIONS ====================
export const optionsService = {
  getAll: async () => {
    // Charger toutes les options puis trier côté client
    const snapshot = await getDocs(collection(db, 'options'));
    const options = docsToArray(snapshot);
    options.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    return { options };
  },

  getById: async (id) => {
    const docRef = await getDoc(doc(db, 'options', id));
    if (!docRef.exists()) return null;
    return { option: { id: docRef.id, ...serializeTimestamps(docRef.data()) } };
  },

  create: async (data) => {
    const docRef = await addDoc(collection(db, 'options'), {
      ...data,
      display_order: data.display_order || 0,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp()
    });
    return { success: true, option: { id: docRef.id, ...data } };
  },

  update: async (id, data) => {
    await updateDoc(doc(db, 'options', id), {
      ...data,
      updated_at: serverTimestamp()
    });
    return { success: true, option: { id, ...data } };
  },

  delete: async (id) => {
    await deleteDoc(doc(db, 'options', id));
    return { success: true };
  },

  reorder: async (orders) => {
    const batch = writeBatch(db);
    orders.forEach(item => {
      const ref = doc(db, 'options', item.id);
      batch.update(ref, {
        display_order: item.display_order || item.order || 0,
        updated_at: serverTimestamp()
      });
    });
    await batch.commit();
    return { success: true };
  }
};

// ==================== PROMOTIONS ====================
export const promotionsService = {
  getAll: async (active_only = false) => {
    let q = collection(db, 'promotions');
    if (active_only) {
      q = query(collection(db, 'promotions'), where('is_active', '==', true));
    }
    const snapshot = await getDocs(q);
    return { promotions: docsToArray(snapshot) };
  },

  getActive: async () => {
    const now = new Date().toISOString().split('T')[0];
    const snapshot = await getDocs(
      query(collection(db, 'promotions'), where('is_active', '==', true))
    );
    // Filtrer par date côté client
    const promotions = docsToArray(snapshot).filter(p => {
      if (!p.start_date || !p.end_date) return true;
      return p.start_date <= now && p.end_date >= now;
    });
    return { promotions };
  },

  getOne: async (id) => {
    const docRef = await getDoc(doc(db, 'promotions', id));
    if (!docRef.exists()) return null;
    return { promotion: { id: docRef.id, ...serializeTimestamps(docRef.data()) } };
  },

  create: async (data) => {
    const docRef = await addDoc(collection(db, 'promotions'), {
      ...data,
      usage_count: 0,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp()
    });
    return { success: true, promotion: { id: docRef.id, ...data } };
  },

  update: async (id, data) => {
    await updateDoc(doc(db, 'promotions', id), {
      ...data,
      updated_at: serverTimestamp()
    });
    return { success: true, promotion: { id, ...data } };
  },

  delete: async (id) => {
    await deleteDoc(doc(db, 'promotions', id));
    return { success: true };
  },

  toggle: async (id, is_active) => {
    await updateDoc(doc(db, 'promotions', id), {
      is_active,
      updated_at: serverTimestamp()
    });
    return { success: true, is_active };
  },

  getMonthlyStats: async (months = 12) => {
    const ordersSnapshot = await getDocs(collection(db, 'orders'));
    const orders = docsToArray(ordersSnapshot).filter(o => o.status !== 'cancelled');

    const monthlyStats = {};
    const now = new Date();

    for (let i = 0; i < months; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`;
      monthlyStats[monthKey] = { month: monthKey, promotions_used: 0, discount_given: 0, revenue_with_promo: 0 };
    }

    orders.forEach(o => {
      const monthKey = o.order_date?.substring(0, 6) || '';
      if (monthlyStats[monthKey] && o.promo_code) {
        monthlyStats[monthKey].promotions_used++;
        monthlyStats[monthKey].discount_given += o.discount_amount || 0;
        monthlyStats[monthKey].revenue_with_promo += o.total || 0;
      }
    });

    const monthly = Object.values(monthlyStats).sort((a, b) => a.month.localeCompare(b.month));
    const totals = {
      total_usage: monthly.reduce((sum, m) => sum + m.promotions_used, 0),
      total_discount: monthly.reduce((sum, m) => sum + m.discount_given, 0),
      total_revenue: monthly.reduce((sum, m) => sum + m.revenue_with_promo, 0)
    };

    return { monthly, totals };
  }
};

// ==================== PROMO CODES (codes promo) ====================
export const promoCodesService = {
  getAll: async () => {
    // BK-46: Limiter à 100 codes promo pour performance
    const snapshot = await getDocs(
      query(collection(db, 'promos'), orderBy('created_at', 'desc'), limit(100))
    );
    return { promos: docsToArray(snapshot) };
  },

  getOne: async (id) => {
    const docRef = await getDoc(doc(db, 'promos', id));
    if (!docRef.exists()) return null;
    return { promo: { id: docRef.id, ...serializeTimestamps(docRef.data()) } };
  },

  create: async (data) => {
    const docRef = await addDoc(collection(db, 'promos'), {
      ...data,
      usage_count: 0,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp()
    });
    return { success: true, promo: { id: docRef.id, ...data } };
  },

  update: async (id, data) => {
    await updateDoc(doc(db, 'promos', id), {
      ...data,
      updated_at: serverTimestamp()
    });
    return { success: true, promo: { id, ...data } };
  },

  delete: async (id) => {
    await deleteDoc(doc(db, 'promos', id));
    return { success: true };
  },

  toggle: async (id, is_active) => {
    await updateDoc(doc(db, 'promos', id), {
      is_active,
      updated_at: serverTimestamp()
    });
    return { success: true };
  }
};

// ==================== ORDERS ====================
export const ordersService = {
  getAll: async (status = null) => {
    // BK-46: Limiter à 200 commandes pour performance
    let q = query(collection(db, 'orders'), orderBy('created_at', 'desc'), limit(200));
    if (status) {
      q = query(collection(db, 'orders'),
        where('status', '==', status),
        orderBy('created_at', 'desc'),
        limit(200)
      );
    }
    const snapshot = await getDocs(q);
    return { orders: docsToArray(snapshot) };
  },

  // Optimisation Build 237: Charger uniquement les commandes d'une période
  getByDateRange: async (startDate, endDate) => {
    const q = query(
      collection(db, 'orders'),
      where('created_at', '>=', startDate),
      where('created_at', '<=', endDate),
      orderBy('created_at', 'desc')
    );
    const snapshot = await getDocs(q);
    return { orders: docsToArray(snapshot) };
  },

  getByDate: async (date) => {
    const snapshot = await getDocs(
      query(collection(db, 'orders'), where('pickup_date', '==', date))
    );
    return { orders: docsToArray(snapshot) };
  },

  getActive: async () => {
    // FIX Build 200: Récupérer TOUTES les commandes récentes, filtrer côté client
    // Évite le besoin d'un index composite Firestore
    const snapshot = await getDocs(
      query(collection(db, 'orders'),
        orderBy('created_at', 'desc'),
        limit(200) // Limiter pour performance
      )
    );
    // Filtrer côté client: exclure les archivées
    const allOrders = docsToArray(snapshot);
    const activeOrders = allOrders.filter(o => o.is_archived !== true);
    return { orders: activeOrders };
  },

  getToday: async () => {
    const today = new Date().toISOString().split('T')[0].replace(/-/g, '');
    const snapshot = await getDocs(
      query(collection(db, 'orders'), where('order_date', '==', today))
    );
    return { orders: docsToArray(snapshot) };
  },

  getOne: async (id) => {
    const docRef = await getDoc(doc(db, 'orders', id));
    if (!docRef.exists()) return null;
    return { order: { id: docRef.id, ...serializeTimestamps(docRef.data()) } };
  },

  // FIX Build 201: Ajout paramètre skipNotification pour les régularisations
  updateStatus: async (id, status, skipNotification = false) => {
    const updateData = {
      status,
      updated_at: serverTimestamp()
    };
    // Si régularisation (jour passé), on ajoute un flag pour que les Cloud Functions ignorent les notifs
    if (skipNotification) {
      updateData.skip_notification = true;
    }
    await updateDoc(doc(db, 'orders', id), updateData);
    return { success: true, status };
  },

  update: async (id, data) => {
    await updateDoc(doc(db, 'orders', id), {
      ...data,
      updated_at: serverTimestamp()
    });
    return { success: true };
  },

  cancel: async (id, reason) => {
    await updateDoc(doc(db, 'orders', id), {
      status: 'cancelled',
      cancel_reason: reason,
      cancelled_at: serverTimestamp(),
      updated_at: serverTimestamp()
    });
    return { success: true };
  },

  // Enregistrer un paiement (encaissement)
  updatePayment: async (id, paymentData) => {
    const updateData = {
      payment_status: 'paid',
      paid_at: serverTimestamp(),
      updated_at: serverTimestamp()
    };

    // Format multi-paiements
    if (paymentData.payments) {
      updateData.payments = paymentData.payments;
      updateData.payment_method = 'multi';
      updateData.amount_received = paymentData.amount_received || 0;
    }
    // Format simple
    else if (paymentData.payment_method) {
      updateData.payment_method = paymentData.payment_method;
      updateData.amount_received = paymentData.amount_received || 0;
    }

    if (paymentData.change_given) {
      updateData.change_given = paymentData.change_given;
    }
    if (paymentData.product_payments) {
      updateData.product_payments = paymentData.product_payments;
    }

    await updateDoc(doc(db, 'orders', id), updateData);
    return { success: true };
  },

  getStats: async () => {
    const today = new Date().toISOString().split('T')[0].replace(/-/g, '');
    const snapshot = await getDocs(
      query(collection(db, 'orders'), where('order_date', '==', today))
    );
    const orders = docsToArray(snapshot);

    const stats = {
      total_orders: orders.length,
      pending: orders.filter(o => o.status === 'pending').length,
      preparing: orders.filter(o => o.status === 'preparing').length,
      ready: orders.filter(o => o.status === 'ready').length,
      completed: orders.filter(o => o.status === 'completed').length,
      cancelled: orders.filter(o => o.status === 'cancelled').length,
      total_revenue: orders
        .filter(o => o.status !== 'cancelled')
        .reduce((sum, o) => sum + (o.total || 0), 0)
    };

    return stats;
  },

  // SEC-07 FIX Build 305: Transaction atomique pour éviter double crédit
  refundMissingItems: async (orderId, missingItemIndices, reason) => {
    const orderRef = doc(db, 'orders', orderId);

    // Pré-lecture pour validation
    const orderSnap = await getDoc(orderRef);
    if (!orderSnap.exists()) throw new Error('Commande non trouvée');
    const order = orderSnap.data();

    // Vérifier que la commande n'a pas déjà été remboursée
    if (order.refund) throw new Error('Cette commande a déjà été remboursée');

    const refundAmount = missingItemIndices.reduce((total, index) => {
      const item = order.items?.[index];
      return total + (item?.total_price || 0);
    }, 0);

    if (refundAmount <= 0) throw new Error('Aucun article sélectionné');

    const customerId = order.customer_uid || order.customer_id;
    if (!customerId) throw new Error('Client non trouvé');

    const customerRef = doc(db, 'customers', customerId);
    const refundedItems = missingItemIndices.map(i => order.items?.[i]?.name || `Article ${i + 1}`);

    // Transaction atomique : crédit fidélité + enregistrement refund
    await runTransaction(db, async (transaction) => {
      const customerSnap = await transaction.get(customerRef);
      if (!customerSnap.exists()) throw new Error('Client non trouvé');

      const orderSnapTx = await transaction.get(orderRef);
      if (orderSnapTx.data().refund) throw new Error('Commande déjà remboursée');

      const currentBalance = customerSnap.data().loyalty_balance || 0;

      transaction.update(customerRef, {
        loyalty_balance: currentBalance + refundAmount,
        updated_at: serverTimestamp()
      });

      transaction.update(orderRef, {
        refund: {
          amount: refundAmount,
          reason: reason,
          items: refundedItems,
          refunded_at: new Date().toISOString()
        },
        updated_at: serverTimestamp()
      });
    });

    return {
      success: true,
      message: `${refundAmount.toFixed(2)}€ remboursés sur la carte de fidélité`,
      refund_amount: refundAmount
    };
  }
};

// ==================== CUSTOMERS ====================
export const customersService = {
  getAll: async () => {
    // BK-46: Limiter à 200 clients pour performance
    const snapshot = await getDocs(
      query(collection(db, 'customers'), orderBy('created_at', 'desc'), limit(200))
    );
    return { customers: docsToArray(snapshot) };
  },

  getOne: async (id) => {
    const docRef = await getDoc(doc(db, 'customers', id));
    if (!docRef.exists()) return null;
    return { customer: { id: docRef.id, ...serializeTimestamps(docRef.data()) } };
  },

  update: async (id, data) => {
    await updateDoc(doc(db, 'customers', id), {
      ...data,
      updated_at: serverTimestamp()
    });
    return { success: true, customer: { id, ...data } };
  },

  getOrders: async (customerId, customerEmail = null, limitCount = 50) => {
    try {
      // FIX Build 207: Essayer d'abord par customer_uid (sans orderBy pour éviter index manquant)
      let snapshot = await getDocs(
        query(collection(db, 'orders'),
          where('customer_uid', '==', customerId),
          limit(limitCount)
        )
      );

      let orders = docsToArray(snapshot);

      // Si aucune commande trouvée et email fourni, essayer par email
      if (orders.length === 0 && customerEmail) {
        console.log(`[getOrders] Aucune commande par UID, essai par email: ${customerEmail}`);
        snapshot = await getDocs(
          query(collection(db, 'orders'),
            where('customer_email', '==', customerEmail),
            limit(limitCount)
          )
        );
        orders = docsToArray(snapshot);
      }

      // Trier côté client par date décroissante
      orders.sort((a, b) => {
        const dateA = a.created_at?.toDate?.() || new Date(a.created_at) || new Date(0);
        const dateB = b.created_at?.toDate?.() || new Date(b.created_at) || new Date(0);
        return dateB - dateA;
      });

      console.log(`[getOrders] ${orders.length} commandes trouvées pour ${customerId}`);
      return { orders };
    } catch (error) {
      console.error('[getOrders] Erreur:', error);
      return { orders: [] };
    }
  },

  getLoyalty: async (id) => {
    const docRef = await getDoc(doc(db, 'customers', id));
    if (!docRef.exists()) return { loyalty_balance: 0 };
    const data = docRef.data();
    return {
      loyalty_balance: data.loyalty_balance || 0,
      total_spent: data.total_spent || 0,
      total_orders: data.total_orders || 0
    };
  },

  addLoyalty: async (id, data) => {
    console.log('=== addLoyalty called ===');
    console.log('Customer ID:', id);
    console.log('Amount:', data.amount);

    const docRef = doc(db, 'customers', id);
    const customerSnap = await getDoc(docRef);

    if (!customerSnap.exists()) {
      console.error('Customer document not found:', id);
      throw new Error('Client introuvable dans la base de données');
    }

    const currentBalance = customerSnap.data().loyalty_balance || 0;
    const newBalance = currentBalance + (data.amount || 0);

    console.log('Current balance:', currentBalance);
    console.log('New balance:', newBalance);

    await setDoc(docRef, {
      loyalty_balance: newBalance,
      updated_at: serverTimestamp()
    }, { merge: true });

    console.log('Loyalty updated successfully');
    return { success: true, new_balance: newBalance };
  },

  getMonthlyRevenue: async () => {
    const now = new Date();
    const thisMonth = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;
    const lastMonth = now.getMonth() === 0
      ? `${now.getFullYear() - 1}12`
      : `${now.getFullYear()}${String(now.getMonth()).padStart(2, '0')}`;

    const ordersSnapshot = await getDocs(collection(db, 'orders'));
    const orders = docsToArray(ordersSnapshot).filter(o => o.status !== 'cancelled');

    let ca_this_month = 0, ca_last_month = 0, orders_this_month = 0;
    orders.forEach(o => {
      const orderMonth = o.order_date?.substring(0, 6) || '';
      if (orderMonth === thisMonth) {
        ca_this_month += o.total || 0;
        orders_this_month++;
      } else if (orderMonth === lastMonth) {
        ca_last_month += o.total || 0;
      }
    });

    return { ca_this_month, ca_last_month, orders_this_month };
  },

  getStatsHistory: async (months = 12) => {
    const ordersSnapshot = await getDocs(collection(db, 'orders'));
    const orders = docsToArray(ordersSnapshot).filter(o => o.status !== 'cancelled');

    const customersSnapshot = await getDocs(collection(db, 'customers'));
    const customers = docsToArray(customersSnapshot);

    // Grouper par mois
    const monthlyStats = {};
    const now = new Date();

    for (let i = 0; i < months; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`;
      // Créer label lisible (ex: "Déc 2024")
      const monthLabel = d.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
      monthlyStats[monthKey] = {
        month: monthKey,
        month_label: monthLabel,
        ca: 0,
        orders: 0,
        new_customers: 0,
        customerSet: new Set() // Pour tracker les clients uniques
      };
    }

    orders.forEach(o => {
      const monthKey = o.order_date?.substring(0, 6) || '';
      if (monthlyStats[monthKey]) {
        monthlyStats[monthKey].ca += o.total || 0;
        monthlyStats[monthKey].orders++;
        // Tracker les clients uniques par mois
        const customerId = o.customer_id || o.customer_email;
        if (customerId) {
          monthlyStats[monthKey].customerSet.add(customerId);
        }
      }
    });

    customers.forEach(c => {
      const createdAt = c.created_at?.toDate?.() || new Date(c.created_at);
      if (createdAt) {
        const monthKey = `${createdAt.getFullYear()}${String(createdAt.getMonth() + 1).padStart(2, '0')}`;
        if (monthlyStats[monthKey]) {
          monthlyStats[monthKey].new_customers++;
        }
      }
    });

    const monthly = Object.values(monthlyStats)
      .sort((a, b) => a.month.localeCompare(b.month))
      .map(m => ({
        month: m.month,
        month_label: m.month_label,
        ca: m.ca,
        orders: m.orders,
        new_customers: m.new_customers,
        avg_order: m.orders > 0 ? m.ca / m.orders : 0,
        unique_customers: m.customerSet.size, // Nombre de clients uniques ce mois
        avg_per_customer: m.customerSet.size > 0 ? m.ca / m.customerSet.size : 0 // CA moyen par client
      }));

    return { monthly };
  },

  getSegmentsStats: async () => {
    const snapshot = await getDocs(collection(db, 'customers'));
    const customers = docsToArray(snapshot);
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    let total = 0, newCount = 0, regulars = 0, vip = 0, inactive = 0, withPushToken = 0;

    customers.forEach(c => {
      total++;
      if (c.push_token || c.expo_push_token) withPushToken++;

      // New: created in last 30 days
      const createdAt = c.created_at?.toDate?.() || new Date(c.created_at);
      if (createdAt && createdAt > thirtyDaysAgo) newCount++;

      // Regulars: 3+ orders
      const ordersCount = c.total_orders || c.orders_count || 0;
      if (ordersCount >= 3) regulars++;

      // VIP: 500€+ spent
      const totalSpent = c.total_spent || 0;
      if (totalSpent >= 500) vip++;

      // Inactive: no order in 30 days or never ordered
      const lastOrder = c.last_order_at?.toDate?.() || (c.last_order_at ? new Date(c.last_order_at) : null);
      if (!lastOrder || lastOrder < thirtyDaysAgo) inactive++;
    });

    return {
      success: true,
      segments: {
        all: total,
        new: newCount,
        regulars,
        vip,
        inactive
      },
      total_customers: total,
      with_push_token: withPushToken
    };
  }
};

// ==================== RESERVATIONS ====================
export const reservationsService = {
  getAll: async () => {
    const snapshot = await getDocs(
      query(collection(db, 'reservations'), orderBy('reservation_date', 'desc'))
    );
    return { reservations: docsToArray(snapshot) };
  },

  getOne: async (id) => {
    const docRef = await getDoc(doc(db, 'reservations', id));
    if (!docRef.exists()) return null;
    return { reservation: { id: docRef.id, ...serializeTimestamps(docRef.data()) } };
  },

  create: async (data) => {
    const docRef = await addDoc(collection(db, 'reservations'), {
      ...data,
      status: 'pending',
      created_at: serverTimestamp()
    });
    return { success: true, reservation: { id: docRef.id, ...data } };
  },

  updateStatus: async (id, data) => {
    await updateDoc(doc(db, 'reservations', id), {
      status: data.status,
      updated_at: serverTimestamp()
    });
    return { success: true };
  },

  delete: async (id) => {
    await deleteDoc(doc(db, 'reservations', id));
    return { success: true };
  }
};

// ==================== NOTIFICATIONS ====================
export const notificationsService = {
  getAll: async () => {
    const snapshot = await getDocs(
      query(collection(db, 'notifications'), orderBy('created_at', 'desc'))
    );
    return { notifications: docsToArray(snapshot) };
  },

  getOne: async (id) => {
    const docRef = await getDoc(doc(db, 'notifications', id));
    if (!docRef.exists()) return null;
    return { notification: { id: docRef.id, ...serializeTimestamps(docRef.data()) } };
  },

  create: async (data) => {
    const status = data.status || 'draft';
    if (!['draft', 'scheduled'].includes(status)) throw new Error('Statut de création invalide');
    if (status === 'scheduled' && (!data.scheduled_at || !Number.isFinite(Date.parse(data.scheduled_at)))) {
      throw new Error('Une date valide est requise pour programmer');
    }
    const payload = { ...data, status,
      scheduled_at: status === 'scheduled' ? new Date(data.scheduled_at).toISOString() : null };
    const docRef = await addDoc(collection(db, 'notifications'), {
      ...payload, created_at: serverTimestamp(), updated_at: serverTimestamp()
    });
    return { success: true, notification: { id: docRef.id, ...payload } };
  },

  update: async (id, data) => {
    const ref = doc(db, 'notifications', id);
    const payload = await runTransaction(db, async (transaction) => {
      const current = await transaction.get(ref);
      if (!current.exists() || !['draft', 'scheduled'].includes(current.data().status)) {
        throw new Error('Seuls les brouillons et programmations non traitées sont modifiables');
      }
      const next = { ...current.data(), ...data };
      if (!['draft', 'scheduled'].includes(next.status)) throw new Error('Statut invalide');
      if (next.status === 'scheduled' && (!next.scheduled_at || !Number.isFinite(Date.parse(next.scheduled_at)))) {
        throw new Error('Une date valide est requise pour programmer');
      }
      const patch = { ...data, status: next.status,
        scheduled_at: next.status === 'scheduled' ? new Date(next.scheduled_at).toISOString() : null };
      transaction.update(ref, { ...patch, updated_at: serverTimestamp() });
      return patch;
    });
    return { success: true, notification: { id, ...payload } };
  },

  delete: async (id) => {
    await runTransaction(db, async (transaction) => {
      const ref = doc(db, 'notifications', id);
      const current = await transaction.get(ref);
      if (current.exists() && ['sending', 'delivery_unknown'].includes(current.data().status)) {
        throw new Error('Conserver cet envoi pour vérification manuelle');
      }
      transaction.delete(ref);
    });
    return { success: true };
  },

  // Étape 1 : le serveur calcule l'aperçu exact et l'audience consentante. Rien n'est envoyé.
  // category 'service' : message de service (tous les appareils) ; sinon promotion (consentants).
  propose: async (id, category = 'marketing') => {
    const fn = httpsCallable(functions, category === 'service' ? 'proposeServiceNotification' : 'proposeMarketingNotification');
    return (await fn({ notification_id: id })).data;
  },

  // Estimation d'audience en lecture seule, calculée par le serveur comme à l'envoi.
  previewAudience: async (category = 'marketing') => {
    const fn = httpsCallable(functions, 'previewMarketingNotification');
    return (await fn({ title: 'Estimation', body: 'Estimation', segment: 'all', category })).data;
  },

  // Étapes 1 et 2 : aperçu, puis accord distinct de l'opérateur sur CET aperçu.
  // confirm(preview) est fourni par l'interface ; un refus n'envoie rien.
  proposeAndSend: async (id, confirm, category = 'marketing') => {
    const preview = await notificationsService.propose(id, category);
    if (!preview.send_ready) return { success: false, cancelled: true, preview, message: category === 'service' ? 'Aucun appareil destinataire' : 'Aucun destinataire consentant' };
    if (!(await confirm(preview))) return { success: false, cancelled: true, preview, message: 'Envoi annulé' };
    return notificationsService.send(id, preview.proposal_digest);
  },

  // Programmation : aperçu, accord distinct, puis lien serveur date + contenu + plafond.
  // Sans cet accord, le cron n'enverra rien.
  proposeAndApproveSchedule: async (id, confirm, category = 'marketing') => {
    const preview = await notificationsService.propose(id, category);
    if (!preview.send_ready) return { approved: false, preview, message: category === 'service' ? 'Aucun appareil destinataire' : 'Aucun destinataire consentant' };
    if (!(await confirm(preview))) return { approved: false, preview, message: 'Programmation non approuvée' };
    const fn = httpsCallable(functions, 'approveScheduledNotification');
    const result = await fn({ notification_id: id, proposal_digest: preview.proposal_digest });
    return { approved: true, preview, ...result.data };
  },

  send: async (id, proposalDigest) => {
    // Le serveur vérifie l'accord, réserve atomiquement le document et gère seul le résultat.
    // Une réponse réseau perdue ne doit jamais transformer un envoi en brouillon réessayable.
    const fn = httpsCallable(functions, 'sendBulkNotification');
    const result = await fn({ notification_id: id, proposal_digest: proposalDigest });
    return {
      ...result.data,
      success: result.data?.status === 'sent',
      sent_count: result.data?.sent || 0,
      message: result.data?.delivery_uncertain ? 'Résultat incertain : vérifier l’historique, ne pas renvoyer' : result.data?.message
    };
  }
};

// ==================== POPUPS ====================
export const popupsService = {
  getAll: async () => {
    const snapshot = await getDocs(
      query(collection(db, 'popups'), orderBy('priority', 'desc'))
    );
    return { popups: docsToArray(snapshot) };
  },

  getActive: async () => {
    const snapshot = await getDocs(
      query(collection(db, 'popups'), where('is_active', '==', true))
    );
    return { popups: docsToArray(snapshot) };
  },

  getOne: async (id) => {
    const docRef = await getDoc(doc(db, 'popups', id));
    if (!docRef.exists()) return null;
    return { popup: { id: docRef.id, ...serializeTimestamps(docRef.data()) } };
  },

  create: async (data) => {
    const docRef = await addDoc(collection(db, 'popups'), {
      ...data,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp()
    });
    return { success: true, popup: { id: docRef.id, ...data } };
  },

  update: async (id, data) => {
    await updateDoc(doc(db, 'popups', id), {
      ...data,
      updated_at: serverTimestamp()
    });
    return { success: true, popup: { id, ...data } };
  },

  delete: async (id) => {
    await deleteDoc(doc(db, 'popups', id));
    return { success: true };
  },

  toggle: async (id, is_active) => {
    await updateDoc(doc(db, 'popups', id), {
      is_active,
      updated_at: serverTimestamp()
    });
    return { success: true, is_active };
  }
};

// ==================== SETTINGS ====================
export const settingsService = {
  get: async () => {
    // IMPORTANT: Les données sont dans settings/restaurant (pas general)
    const docRef = await getDoc(doc(db, 'settings', 'restaurant'));
    if (!docRef.exists()) return { settings: {} };
    return { settings: serializeTimestamps(docRef.data()) };
  },

  update: async (data) => {
    // IMPORTANT: Sauvegarder dans settings/restaurant
    await setDoc(doc(db, 'settings', 'restaurant'), {
      ...data,
      updated_at: serverTimestamp()
    }, { merge: true });
    return { success: true };
  }
};

// ==================== CHOICE LIBRARY ====================
export const choiceLibraryService = {
  getAll: async (category = null) => {
    let q = collection(db, 'choice_library');
    if (category) {
      q = query(collection(db, 'choice_library'), where('category', '==', category));
    }
    const snapshot = await getDocs(q);
    return { choices: docsToArray(snapshot) };
  },

  getCategories: async () => {
    const snapshot = await getDocs(collection(db, 'choice_library'));
    const categories = new Set();
    snapshot.docs.forEach(doc => {
      const cat = doc.data().category;
      if (cat) categories.add(cat);
    });
    return { categories: Array.from(categories).sort() };
  },

  getById: async (id) => {
    const docRef = await getDoc(doc(db, 'choice_library', id));
    if (!docRef.exists()) return null;
    return { choice: { id: docRef.id, ...serializeTimestamps(docRef.data()) } };
  },

  // Recherche par nom (pour sync bibliothèque)
  getByName: async (name) => {
    const q = query(
      collection(db, 'choice_library'),
      where('name', '==', name),
      limit(1)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
  },

  create: async (data) => {
    const docRef = await addDoc(collection(db, 'choice_library'), {
      ...data,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp()
    });
    return { success: true, choice: { id: docRef.id, ...data } };
  },

  update: async (id, data) => {
    // Récupérer l'ancien nom AVANT la mise à jour
    const oldChoice = await getDoc(doc(db, 'choice_library', id));
    const oldName = oldChoice.exists() ? oldChoice.data().name : null;

    await updateDoc(doc(db, 'choice_library', id), {
      ...data,
      updated_at: serverTimestamp()
    });

    // Sync avec les options qui utilisent ce choix
    let synced_options = 0;
    if (oldName && data.image_url) {
      synced_options = await syncChoiceToOptions(oldName, data) || 0;
    }

    return { success: true, choice: { id, ...data }, synced_options };
  },

  delete: async (id) => {
    await deleteDoc(doc(db, 'choice_library', id));
    return { success: true };
  }
};

// Helper pour sync choices → options
// FIX Build 214: Utiliser match EXACT au lieu de .includes() pour éviter les faux positifs
const syncChoiceToOptions = async (choiceName, updateData) => {
  if (!updateData.image_url) return;

  const optionsSnapshot = await getDocs(collection(db, 'options'));
  const batch = writeBatch(db);
  let synced = 0;

  // Normaliser le nom pour comparaison exacte
  const normalizedChoiceName = choiceName.toUpperCase().trim();

  optionsSnapshot.docs.forEach(optDoc => {
    const optData = optDoc.data();
    let updated = false;
    const choices = (optData.choices || []).map(c => {
      // Match EXACT (pas .includes() qui causerait des faux positifs)
      const normalizedName = (c.name || '').toUpperCase().trim();
      if (normalizedName === normalizedChoiceName) {
        updated = true;
        return { ...c, image_url: updateData.image_url };
      }
      return c;
    });

    if (updated) {
      batch.update(optDoc.ref, { choices, updated_at: serverTimestamp() });
      synced++;
    }
  });

  if (synced > 0) {
    await batch.commit();
  }
  return synced;
};

// FIX Build 214: Sync COMPLÈTE Bibliothèque → Options (par nom EXACT)
// FIX Build 215: Inclut les SOUS-OPTIONS (récursif)
// Cette fonction met à jour TOUTES les options avec les images de la bibliothèque
export const syncAllLibraryToOptions = async () => {
  console.log('[SYNC] ========================================');
  console.log('[SYNC] Démarrage sync complète Bibliothèque → Options (+ sous-options)...');

  // 1. Charger toute la bibliothèque
  const librarySnapshot = await getDocs(collection(db, 'choice_library'));
  const libraryMap = new Map();
  const libraryDebug = [];

  librarySnapshot.docs.forEach(doc => {
    const data = doc.data();
    const name = data.name || '';
    const imageUrl = data.image_url || '';

    // Log les entrées problématiques
    if (name.toUpperCase().includes('TOMATE') ||
        name.toUpperCase().includes('SALADE') ||
        name.toUpperCase().includes('CHEDDAR') ||
        name.toUpperCase().includes('REBLOCHON')) {
      console.log(`[LIBRARY] "${name}" → ${imageUrl ? imageUrl.substring(0, 60) + '...' : 'PAS D\'IMAGE!'}`);
      libraryDebug.push({ name, hasImage: !!imageUrl });
    }

    if (name && imageUrl) {
      libraryMap.set(name.toUpperCase().trim(), imageUrl);
    }
  });

  console.log(`[SYNC] ${libraryMap.size} choix avec images dans la bibliothèque`);
  console.log(`[SYNC] Choix problématiques dans library:`, libraryDebug);

  // Compteurs pour stats
  let totalUpdated = 0;
  let subOptionsUpdated = 0;
  let optionsUpdated = 0;
  let notFoundInLibrary = [];

  // FIX Build 215: Fonction récursive pour traiter les sous-options
  const syncChoicesRecursive = (choices, depth = 0) => {
    if (!choices || !Array.isArray(choices)) return { choices: [], changed: false };

    let hasChanges = false;
    const prefix = '  '.repeat(depth);

    const updatedChoices = choices.map(choice => {
      if (!choice.name) return choice;

      const key = choice.name.toUpperCase().trim();
      const libraryImageUrl = libraryMap.get(key);
      let updatedChoice = { ...choice };

      // Log les choix problématiques
      if (key.includes('TOMATE') || key.includes('SALADE') ||
          key.includes('CHEDDAR') || key.includes('REBLOCHON')) {
        if (!libraryImageUrl) {
          console.log(`${prefix}[CHOICE] "${choice.name}" → PAS TROUVÉ dans bibliothèque!`);
          if (!notFoundInLibrary.includes(choice.name)) {
            notFoundInLibrary.push(choice.name);
          }
        } else if (libraryImageUrl === choice.image_url) {
          console.log(`${prefix}[CHOICE] "${choice.name}" → déjà la bonne image`);
        } else {
          console.log(`${prefix}[CHOICE] "${choice.name}" → MISE À JOUR!`);
        }
      }

      // Si trouvé dans la bibliothèque ET image différente → mettre à jour
      if (libraryImageUrl && libraryImageUrl !== choice.image_url) {
        hasChanges = true;
        if (depth === 0) {
          totalUpdated++;
        } else {
          subOptionsUpdated++;
        }
        updatedChoice.image_url = libraryImageUrl;
        console.log(`${prefix}[SYNC] ✅ "${choice.name}" image mise à jour (niveau ${depth})`);
      }

      // FIX Build 215: Traiter récursivement les sous-options
      if (choice.sub_options && Array.isArray(choice.sub_options) && choice.sub_options.length > 0) {
        console.log(`${prefix}[SUB-OPTIONS] Traitement de ${choice.sub_options.length} sous-option(s) pour "${choice.name}"...`);

        const updatedSubOptions = choice.sub_options.map(subOption => {
          if (!subOption.choices || !Array.isArray(subOption.choices)) return subOption;

          const result = syncChoicesRecursive(subOption.choices, depth + 1);
          if (result.changed) {
            hasChanges = true;
            return { ...subOption, choices: result.choices };
          }
          return subOption;
        });

        updatedChoice.sub_options = updatedSubOptions;
      }

      return updatedChoice;
    });

    return { choices: updatedChoices, changed: hasChanges };
  };

  // 2. Parcourir toutes les options et mettre à jour
  const optionsSnapshot = await getDocs(collection(db, 'options'));

  for (const optDoc of optionsSnapshot.docs) {
    const optData = optDoc.data();

    console.log(`\n[OPTION] Traitement de "${optData.name}" (${(optData.choices || []).length} choix)...`);

    const result = syncChoicesRecursive(optData.choices || []);

    if (result.changed) {
      await updateDoc(optDoc.ref, {
        choices: result.choices,
        updated_at: serverTimestamp()
      });
      optionsUpdated++;
      console.log(`[OPTION] ✅ "${optData.name}" mise à jour!`);
    }
  }

  console.log('\n[SYNC] ========================================');
  console.log(`[SYNC] Terminé!`);
  console.log(`[SYNC] - ${totalUpdated} choix principaux mis à jour`);
  console.log(`[SYNC] - ${subOptionsUpdated} sous-options mises à jour`);
  console.log(`[SYNC] - ${optionsUpdated} options modifiées au total`);
  console.log(`[SYNC] Non trouvés dans bibliothèque:`, notFoundInLibrary);
  console.log('[SYNC] ========================================');

  return {
    totalUpdated: totalUpdated + subOptionsUpdated,
    optionsUpdated,
    subOptionsUpdated,
    notFoundInLibrary
  };
};

// ==================== UPSELL ====================
export const upsellService = {
  getAll: async () => {
    const snapshot = await getDocs(
      query(collection(db, 'upsell_products'), orderBy('display_order', 'asc'))
    );

    const upsells = [];
    for (const upsellDoc of snapshot.docs) {
      const upsellData = upsellDoc.data();
      const productDoc = await getDoc(doc(db, 'products', upsellData.product_id));
      if (productDoc.exists()) {
        upsells.push({
          id: upsellDoc.id,
          product_id: upsellData.product_id,
          display_order: upsellData.display_order || 0,
          product: { id: productDoc.id, ...serializeTimestamps(productDoc.data()) }
        });
      }
    }
    return { upsells };
  },

  add: async (productId) => {
    // Trouver le display_order max
    const snapshot = await getDocs(collection(db, 'upsell_products'));
    const maxOrder = snapshot.docs.reduce((max, d) =>
      Math.max(max, d.data().display_order || 0), 0);

    const docRef = await addDoc(collection(db, 'upsell_products'), {
      product_id: productId,
      display_order: maxOrder + 1,
      created_at: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  },

  remove: async (id) => {
    await deleteDoc(doc(db, 'upsell_products', id));
    return { success: true };
  },

  updateOrder: async (items) => {
    const batch = writeBatch(db);
    items.forEach((item, index) => {
      const ref = doc(db, 'upsell_products', item.id);
      batch.update(ref, { display_order: index });
    });
    await batch.commit();
    return { success: true };
  }
};

// ==================== UPLOAD (Firebase Storage) ====================
export const uploadService = {
  uploadImage: async (file, folder = 'images') => {
    const filename = `${folder}/${Date.now()}_${file.name || 'image.png'}`;
    const storageRef = ref(storage, filename);

    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);

    // Ajouter timestamp pour éviter le cache navigateur/app
    const urlWithCacheBuster = `${url}&_t=${Date.now()}`;
    return { url: urlWithCacheBuster, path: filename };
  },

  productImage: async (file) => {
    return uploadService.uploadImage(file, 'products');
  },

  categoryImage: async (file) => {
    return uploadService.uploadImage(file, 'categories');
  },

  popupImage: async (file) => {
    return uploadService.uploadImage(file, 'popups');
  },

  choiceImage: async (file) => {
    return uploadService.uploadImage(file, 'choices');
  },

  deleteImage: async (path) => {
    try {
      // Si c'est une URL complète, extraire le path
      let storagePath = path;
      if (path.includes('firebasestorage.googleapis.com')) {
        const match = path.match(/\/o\/(.+?)\?/);
        if (match) storagePath = decodeURIComponent(match[1]);
      }
      const storageRef = ref(storage, storagePath);
      await deleteObject(storageRef);
      return { success: true };
    } catch (error) {
      console.warn('Delete image error:', error);
      return { success: false, error: error.message };
    }
  }
};

// ==================== SURPRISE DU JOUR ====================
export const surpriseService = {
  getConfig: async () => {
    const docRef = await getDoc(doc(db, 'settings', 'surprise_du_jour'));
    if (!docRef.exists()) return { config: {} };
    return { config: serializeTimestamps(docRef.data()) };
  },

  updateConfig: async (data) => {
    await updateDoc(doc(db, 'settings', 'surprise_du_jour'), {
      ...data,
      updated_at: serverTimestamp()
    });
    return { success: true };
  },

  getRewards: async () => {
    const snapshot = await getDocs(collection(db, 'surprise_rewards'));
    return { rewards: docsToArray(snapshot) };
  },

  createReward: async (data) => {
    const docRef = await addDoc(collection(db, 'surprise_rewards'), {
      ...data,
      created_at: serverTimestamp()
    });
    return { success: true, reward: { id: docRef.id, ...data } };
  },

  updateReward: async (id, data) => {
    await updateDoc(doc(db, 'surprise_rewards', id), data);
    return { success: true };
  },

  deleteReward: async (id) => {
    await deleteDoc(doc(db, 'surprise_rewards', id));
    return { success: true };
  }
};

// ==================== CHRONO GAME ====================
export const chronoGameService = {
  getConfig: async () => {
    const docRef = await getDoc(doc(db, 'settings', 'chrono_game'));
    if (!docRef.exists()) return { config: { enabled: false, hidden: false } };
    return { config: serializeTimestamps(docRef.data()) };
  },

  updateConfig: async (data) => {
    // Utiliser setDoc avec merge pour créer ou mettre à jour
    await setDoc(doc(db, 'settings', 'chrono_game'), {
      ...data,
      updated_at: serverTimestamp()
    }, { merge: true });
    return { success: true };
  },

  getStats: async () => {
    // Calculer les stats depuis chrono_plays
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    try {
      const playsSnapshot = await getDocs(collection(db, 'chrono_plays'));
      let totalPlays = 0;
      let totalWins = 0;
      let todayPlays = 0;
      let todayWins = 0;

      playsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        totalPlays++;
        // La Cloud Function ecrit le champ "won" (et "created_at").
        // On reste tolerant aux anciens champs "is_winner"/"played_at".
        const isWin = data.won === true || data.is_winner === true;
        if (isWin) totalWins++;

        const rawDate = data.created_at || data.played_at;
        const playDate = rawDate?.toDate?.() || (rawDate ? new Date(rawDate) : null);
        if (playDate && playDate >= today) {
          todayPlays++;
          if (isWin) todayWins++;
        }
      });

      return {
        today: {
          plays: todayPlays,
          wins: todayWins,
          win_rate: todayPlays > 0 ? Math.round((todayWins / todayPlays) * 100) : 0
        },
        total: {
          plays: totalPlays,
          wins: totalWins,
          win_rate: totalPlays > 0 ? Math.round((totalWins / totalPlays) * 100) : 0
        }
      };
    } catch (error) {
      console.error('Erreur getStats chrono:', error);
      return { today: { plays: 0, wins: 0, win_rate: 0 }, total: { plays: 0, wins: 0, win_rate: 0 } };
    }
  },

  // Stats joueurs Chrono : derniers joueurs + classement + meilleurs temps, avec les NOMS clients
  getPlayerStats: async () => {
    try {
      const [playsSnap, customersSnap] = await Promise.all([
        getDocs(collection(db, 'chrono_plays')),
        getDocs(collection(db, 'customers')),
      ]);

      // Map uid -> nom lisible (fallback email puis "Client")
      const nameById = {};
      customersSnap.docs.forEach(d => {
        const c = d.data();
        const full = [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
        nameById[d.id] = full || c.name || c.email || 'Client';
      });

      const plays = playsSnap.docs.map(d => {
        const p = d.data();
        const date = p.created_at?.toDate?.() || (p.created_at ? new Date(p.created_at) : null);
        const won = p.won === true || p.is_winner === true;
        // reward_granted absent (anciennes parties) => on considere le cadeau comme donne.
        // false => victoire bloquee par la limite de gains/jour (aucun cadeau).
        const rewarded = won && p.reward_granted !== false;
        return {
          customer_id: p.customer_id,
          name: nameById[p.customer_id] || 'Client',
          time: typeof p.time === 'number' ? p.time : null,
          won,
          rewarded,
          date,
        };
      });

      const toISO = (dt) => (dt ? dt.toISOString() : null);

      // Derniers joueurs (25 plus recents)
      const recent = plays
        .filter(p => p.date)
        .sort((a, b) => b.date - a.date)
        .slice(0, 25)
        .map(p => ({ name: p.name, time: p.time, won: p.won, rewarded: p.rewarded, date: toISO(p.date) }));

      // Classement par joueur (parties / fois a 10s / cadeaux reellement donnes / meilleur ecart)
      const byPlayer = {};
      plays.forEach(p => {
        if (!p.customer_id) return;
        if (!byPlayer[p.customer_id]) {
          byPlayer[p.customer_id] = { name: p.name, plays: 0, wins: 0, rewards: 0, bestDiff: null };
        }
        const e = byPlayer[p.customer_id];
        e.plays++;
        if (p.won) e.wins++;
        if (p.rewarded) e.rewards++;
        if (typeof p.time === 'number') {
          const diff = Math.abs(p.time - 10);
          if (e.bestDiff === null || diff < e.bestDiff) e.bestDiff = diff;
        }
      });
      const leaderboard = Object.values(byPlayer)
        .sort((a, b) => b.plays - a.plays)
        .slice(0, 10);

      // Meilleurs temps (plus proches de 10.00s)
      const bestTimes = plays
        .filter(p => typeof p.time === 'number')
        .sort((a, b) => Math.abs(a.time - 10) - Math.abs(b.time - 10))
        .slice(0, 10)
        .map(p => ({ name: p.name, time: p.time, won: p.won, rewarded: p.rewarded, date: toISO(p.date) }));

      return { recent, leaderboard, bestTimes, uniquePlayers: Object.keys(byPlayer).length };
    } catch (error) {
      console.error('Erreur getPlayerStats chrono:', error);
      return { recent: [], leaderboard: [], bestTimes: [], uniquePlayers: 0 };
    }
  }
};

// ==================== TICKET Z (via Cloud Functions) ====================
export const ticketZService = {
  getPendingDays: async () => {
    try {
      const fn = httpsCallable(functions, 'getPendingDays');
      const result = await fn({});
      return { pending_days: result.data };
    } catch (error) {
      console.error('Erreur getPendingDays:', error);
      return { pending_days: [] };
    }
  },

  getDailyStatus: async (date) => {
    try {
      const fn = httpsCallable(functions, 'getDailyStatus');
      const result = await fn({ date });
      return result.data;
    } catch (error) {
      console.error('Erreur getDailyStatus:', error);
      return { date, is_closed: false, pending_orders: 0, unpaid_orders: 0, can_close: false };
    }
  },

  closeDay: async (date, options = {}) => {
    try {
      const fn = httpsCallable(functions, 'createTicketZ');
      const result = await fn({
        date,
        force_close: options.force_close || false,
        pin_code: options.pin_code || null
      });
      return { success: true, ticket_z: result.data };
    } catch (error) {
      console.error('Erreur closeDay:', error);
      throw error;
    }
  },

  list: async (limit = 30) => {
    try {
      const fn = httpsCallable(functions, 'listTicketsZ');
      const result = await fn({ limit });
      return { tickets: result.data };
    } catch (error) {
      console.error('Erreur listTicketsZ:', error);
      return { tickets: [] };
    }
  }
};

// ==================== DASHBOARD ====================
export const dashboardService = {
  getStats: async () => {
    const today = new Date().toISOString().split('T')[0].replace(/-/g, '');

    // Commandes du jour
    const ordersSnapshot = await getDocs(
      query(collection(db, 'orders'), where('order_date', '==', today))
    );
    const orders = docsToArray(ordersSnapshot);
    const validOrders = orders.filter(o => o.status !== 'cancelled');

    // CA du jour
    const ca_today = validOrders.reduce((sum, o) => sum + (o.total || 0), 0);

    // Répartition des paiements
    const payment_breakdown = {};
    let loyalty_used_today = 0;
    validOrders.forEach(o => {
      if (o.payments && Array.isArray(o.payments)) {
        o.payments.forEach(p => {
          const method = p.method || 'other';
          payment_breakdown[method] = (payment_breakdown[method] || 0) + (p.amount || 0);
        });
      } else if (o.payment_method) {
        const method = o.payment_method;
        payment_breakdown[method] = (payment_breakdown[method] || 0) + (o.total || 0);
      }
      // Fidélité utilisée
      if (o.loyalty_used) {
        loyalty_used_today += o.loyalty_used;
      }
    });

    // Produits en rupture
    const productsSnapshot = await getDocs(collection(db, 'products'));
    const products = docsToArray(productsSnapshot);
    const out_of_stock_count = products.filter(p => p.is_out_of_stock).length;

    // Top 5 produits (7 jours)
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const weekAgoStr = sevenDaysAgo.toISOString().split('T')[0].replace(/-/g, '');

    const weekOrdersSnapshot = await getDocs(
      query(collection(db, 'orders'), where('order_date', '>=', weekAgoStr))
    );
    const weekOrders = docsToArray(weekOrdersSnapshot).filter(o => o.status !== 'cancelled');

    const productStats = {};
    weekOrders.forEach(o => {
      (o.items || []).forEach(item => {
        const name = item.name || 'Inconnu';
        if (!productStats[name]) {
          productStats[name] = { name, quantity: 0, revenue: 0 };
        }
        productStats[name].quantity += lineQuantity(item);
        productStats[name].revenue += lineRevenue(item);
      });
    });
    const top_products = Object.values(productStats)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);

    return {
      ca_today,
      orders_today: orders.length,
      payment_breakdown,
      loyalty_used_today,
      alerts: { out_of_stock_count },
      top_products
    };
  }
};

// ==================== AI MARKETING (Cloud Functions) ====================
export const aiService = {
  generateMarketing: async (data) => {
    try {
      const fn = httpsCallable(functions, 'aiGenerateMarketing');
      const result = await fn(data);
      return { data: result.data };
    } catch (error) {
      console.error('Erreur aiGenerateMarketing:', error);
      throw error;
    }
  },

  analyzeSales: async () => {
    try {
      const fn = httpsCallable(functions, 'aiAnalyzeSales');
      const result = await fn({});
      return { data: result.data };
    } catch (error) {
      console.error('Erreur aiAnalyzeSales:', error);
      throw error;
    }
  },

  chat: async (question) => {
    try {
      const fn = httpsCallable(functions, 'aiChatMarketing');
      const result = await fn({ question });
      return { data: result.data };
    } catch (error) {
      console.error('Erreur aiChatMarketing:', error);
      throw error;
    }
  },

  suggestPromo: async () => {
    try {
      const fn = httpsCallable(functions, 'aiSuggestPromo');
      const result = await fn({});
      return { data: result.data };
    } catch (error) {
      console.error('Erreur aiSuggestPromo:', error);
      throw error;
    }
  }
};

// ==================== AI CAMPAIGNS (Cloud Functions) ====================
export const aiCampaignsService = {
  getCampaigns: async (status = null) => {
    try {
      const fn = httpsCallable(functions, 'getAICampaigns');
      const result = await fn({ status });
      return result.data;
    } catch (error) {
      console.error('Erreur getAICampaigns:', error);
      return { campaigns: [] };
    }
  },

  generateCampaigns: async (forceRegenerate = false) => {
    try {
      const fn = httpsCallable(functions, 'generateAICampaigns');
      const result = await fn({ force_regenerate: forceRegenerate });
      return result.data;
    } catch (error) {
      console.error('Erreur generateAICampaigns:', error);
      throw error;
    }
  },

  validateCampaign: async (campaignId, accepted, notes = '') => {
    try {
      const fn = httpsCallable(functions, 'validateAICampaign');
      const result = await fn({ campaign_id: campaignId, accepted, notes });
      return result.data;
    } catch (error) {
      console.error('Erreur validateAICampaign:', error);
      throw error;
    }
  },

  getStats: async () => {
    try {
      const fn = httpsCallable(functions, 'getAIMarketingStats');
      const result = await fn({});
      return result.data;
    } catch (error) {
      console.error('Erreur getAIMarketingStats:', error);
      return { total_campaigns: 0, accepted: 0, refused: 0, pending: 0, acceptance_rate: 0 };
    }
  },

  // AI Marketing Settings (stored in settings/ai_marketing)
  getSettings: async () => {
    try {
      const docRef = doc(db, 'settings', 'ai_marketing');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return docSnap.data();
      }
      // Default settings
      return {
        max_suggestions_per_week: 5,
        max_promo_budget_percent: 15.0,
        excluded_product_ids: [],
        excluded_category_ids: [],
        priority_objectives: ['boost_ca', 'fideliser', 'reactiver', 'augmenter_panier'],
        allowed_offer_types: ['bogo', 'percentage', 'fidelity', 'happy_hour', 'reactivation']
      };
    } catch (error) {
      console.error('Erreur getAIMarketingSettings:', error);
      throw error;
    }
  },

  saveSettings: async (settings) => {
    try {
      const docRef = doc(db, 'settings', 'ai_marketing');
      await setDoc(docRef, {
        ...settings,
        updated_at: serverTimestamp()
      }, { merge: true });
      return { success: true };
    } catch (error) {
      console.error('Erreur saveAIMarketingSettings:', error);
      throw error;
    }
  }
};

// ==================== PAYMENTS (via Cloud Functions) ====================
export const paymentsService = {
  verifyStripePayment: async (orderId) => {
    try {
      // 1. Récupérer la commande pour obtenir le stripe_payment_intent_id
      const orderDoc = await getDoc(doc(db, 'orders', orderId));
      if (!orderDoc.exists()) {
        return { updated: false, message: 'Commande introuvable' };
      }

      const orderData = orderDoc.data();
      const paymentIntentId = orderData.stripe_payment_intent_id;

      if (!paymentIntentId) {
        return { updated: false, message: 'Pas de PaymentIntent associé à cette commande' };
      }

      // 2. Appeler la Cloud Function verifyPayment
      const fn = httpsCallable(functions, 'verifyPayment');
      const result = await fn({ paymentIntentId, orderId });

      // 3. Retourner le résultat dans le format attendu
      const wasUpdated = result.data.succeeded && orderData.payment_status !== 'paid';
      return {
        updated: wasUpdated,
        message: wasUpdated ? 'Paiement vérifié et mis à jour' : 'Aucun changement nécessaire',
        stripe_status: result.data.status
      };
    } catch (error) {
      console.error('Erreur verifyStripePayment:', error);
      throw error;
    }
  }
};

// Export groupé pour faciliter l'import
export default {
  categories: categoriesService,
  products: productsService,
  options: optionsService,
  promotions: promotionsService,
  promoCodes: promoCodesService,
  orders: ordersService,
  customers: customersService,
  popups: popupsService,
  settings: settingsService,
  choiceLibrary: choiceLibraryService,
  upsell: upsellService,
  upload: uploadService,
  surprise: surpriseService,
  chronoGame: chronoGameService,
  ticketZ: ticketZService,
  dashboard: dashboardService,
  notifications: notificationsService,
  reservations: reservationsService,
  ai: aiService,
  aiCampaigns: aiCampaignsService,
  payments: paymentsService
};
